// PAGAR SIN SALIR DE SOLAR HUB: el QR Bancolombia va PRIMERO (dueño, 13-oct-2026).
//
// El QR de Wompi cobra 1 % frente al 2,65 % + $700 del resto y se paga desde la app de
// Bancolombia o Nequi. «Otros medios» sigue por el checkout de Wompi de siempre.
//
// 🪤 En el MISMO celular no se puede escanear la propia pantalla: «Guardar QR» deja la imagen en la
//    galería (PNG; Wompi la entrega en SVG, que las galerías no muestran) y la app del banco la
//    carga desde ahí.
//
// El QR solo lo aceptan personas naturales con cuenta Bancolombia o Nequi: por eso «Otros medios»
// está siempre a un toque. La confirmación la hace el servidor (`GET /pagos/qr` liquida con el
// mismo `liquidar` del webhook y del cron); acá solo se espera.
import React from 'react';
import { C } from '../constants';
import { terminosPagoQr, estadoPagoQr } from '../services/pagos';

const CADA_MS = 3000;
const TOPE_MS = 15 * 60 * 1000;

function srcDeImagen(b64) {
  return `data:${b64.startsWith('iVBOR') ? 'image/png' : 'image/svg+xml'};base64,${b64}`;
}

async function qrComoPng(src) {
  const img = new Image();
  await new Promise((ok, mal) => { img.onload = () => ok(); img.onerror = () => mal(new Error('imagen')); img.src = src; });
  const lado = 1024;
  const lienzo = document.createElement('canvas');
  lienzo.width = lado; lienzo.height = lado;
  const ctx = lienzo.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, lado, lado);
  ctx.drawImage(img, 48, 48, lado - 96, lado - 96);
  return new Promise((ok, mal) => lienzo.toBlob((b) => (b ? ok(b) : mal(new Error('png'))), 'image/png'));
}

const caja = { background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, width: '100%', maxWidth: 420 };
const boton = (primario) => ({
  border: primario ? 'none' : `1px solid ${C.border}`, background: primario ? C.teal : 'transparent',
  color: primario ? '#fff' : 'inherit', borderRadius: 8, padding: '9px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 13,
});

/**
 * @param {object} p
 * @param {boolean} p.abierto
 * @param {() => void} p.onOtrosMedios  el checkout de siempre
 * @param {() => Promise<{ref?: string, error?: string}>} p.onGenerarQr  abre el cobro con `qr: true`
 * @param {() => void} p.onPagado
 * @param {() => void} p.onCerrar
 */
export default function PagoQr({ abierto, onOtrosMedios, onGenerarQr, onPagado, onCerrar }) {
  const [paso, setPaso] = React.useState('elegir');
  const [terminos, setTerminos] = React.useState(null);
  const [acepto, setAcepto] = React.useState(false);
  const [img, setImg] = React.useState(null);
  const [error, setError] = React.useState('');
  // 🔴 Mientras el QR está vivo no se abre otro medio (dueño, 13-oct-2026): un QR sin pagar sigue
  // cobrable ~15 min y pagar por otro lado sería pagar dos veces. Sin «Volver», sin «Otros medios»,
  // sin cerrar, y con la hora a la que vence. El servidor tiene la misma guarda (409 `qr_pendiente`).
  const [venceEn, setVenceEn] = React.useState(null);
  const [ahora, setAhora] = React.useState(() => Date.now());
  const vivo = React.useRef(0);

  React.useEffect(() => {
    vivo.current += 1;
    if (abierto) { setPaso('elegir'); setAcepto(false); setImg(null); setError(''); setVenceEn(null); }
  }, [abierto]);
  React.useEffect(() => () => { vivo.current += 1; }, []);
  React.useEffect(() => {
    if (paso !== 'qr') return undefined;
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [paso]);

  if (!abierto) return null;
  const qrVivo = paso === 'generando' || paso === 'qr';
  const cerrar = () => { if (qrVivo) return; vivo.current += 1; onCerrar(); };
  const horaVence = venceEn ? new Date(venceEn).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }) : '';
  const quedan = venceEn ? Math.max(0, Math.round((venceEn - ahora) / 1000)) : 0;
  const reloj = `${Math.floor(quedan / 60)}:${String(quedan % 60).padStart(2, '0')}`;

  const elegirQr = async () => {
    setError(''); setPaso('terminos');
    if (terminos) return;
    try {
      const r = await terminosPagoQr();
      if (!r?.terminos_url) throw new Error(r?.error || 'Wompi no respondió.');
      setTerminos(r);
    } catch (e) {
      setError((e && e.message) || 'Wompi no respondió.');
    }
  };

  const generar = async () => {
    const yo = ++vivo.current;
    setPaso('generando'); setError('');
    const g = await onGenerarQr();
    if (vivo.current !== yo) return;
    if (!g?.ref) { setError(g?.error || 'No se pudo generar el QR. Paga con otro medio.'); setPaso('fallo'); return; }
    const inicio = Date.now();
    setVenceEn(g.vence && Number.isFinite(Date.parse(g.vence)) ? Date.parse(g.vence) : inicio + TOPE_MS);
    while (vivo.current === yo) {
      const d = await estadoPagoQr(g.ref).catch(() => null);
      if (vivo.current !== yo) return;
      if (d?.estado === 'aprobada') { setPaso('pagado'); onPagado(); return; }
      if (d?.estado === 'rechazada') {
        setError('Tu banco no aprobó el pago con QR. Puedes pagar con otro medio.');
        setPaso('fallo');
        return;
      }
      if (d?.vence && Number.isFinite(Date.parse(d.vence))) setVenceEn(Date.parse(d.vence));
      if (d?.qr_imagen) { setImg(srcDeImagen(d.qr_imagen)); setPaso('qr'); }
      if (Date.now() - inicio > TOPE_MS) {
        setError('El QR venció sin pago. Si ya pagaste, lo confirmamos en unos minutos; si no, paga con otro medio.');
        setPaso('fallo');
        return;
      }
      await new Promise((r) => setTimeout(r, CADA_MS));
    }
  };

  const guardar = async () => {
    if (!img) return;
    try {
      const png = await qrComoPng(img);
      const nombre = 'pago-solarhub.png';
      const archivo = new File([png], nombre, { type: 'image/png' });
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [archivo] })) {
        try { await navigator.share({ files: [archivo], title: 'QR de pago Solar Hub' }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
      }
      const url = URL.createObjectURL(png);
      const a = document.createElement('a');
      a.href = url; a.download = nombre;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (_) {
      setError('No se pudo guardar la imagen. Toma una captura de pantalla del QR.');
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget && !qrVivo) cerrar(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={caja}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.heading }}>{paso === 'pagado' ? 'Pago recibido' : '¿Cómo quieres pagar?'}</div>
          {!qrVivo && <button type="button" onClick={cerrar} aria-label="Cerrar" style={{ ...boton(false), padding: '4px 10px' }}>✕</button>}
        </div>

        {paso === 'elegir' && (
          <div style={{ display: 'grid', gap: 8 }}>
            <button type="button" onClick={elegirQr} style={{ textAlign: 'left', border: `2px solid ${C.teal}`, background: `${C.teal}12`, color: 'inherit', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
                Código QR · Bancolombia o Nequi
                <span style={{ marginLeft: 'auto', background: C.teal, color: '#fff', borderRadius: 6, padding: '2px 6px', fontSize: 10 }}>Recomendado</span>
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Pagas aquí mismo, sin salir de Solar Hub, desde la app de tu banco.</div>
            </button>
            <button type="button" onClick={() => { cerrar(); onOtrosMedios(); }} style={{ textAlign: 'left', border: `1px solid ${C.border}`, background: 'transparent', color: 'inherit', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
              <div style={{ fontWeight: 700 }}>Otros medios</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Tarjeta, PSE, Nequi, Daviplata… en la página segura de Wompi.</div>
            </button>
          </div>
        )}

        {paso === 'terminos' && (
          <div style={{ fontSize: 13, display: 'grid', gap: 10 }}>
            {!terminos && !error && <div style={{ color: C.muted }}>Consultando a Wompi…</div>}
            {terminos && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  Acepto los <a href={terminos.terminos_url} target="_blank" rel="noreferrer">términos y condiciones</a>
                  {terminos.datos_url && <> y la <a href={terminos.datos_url} target="_blank" rel="noreferrer">autorización de datos personales</a></>} de Wompi.
                </span>
              </label>
            )}
            <div style={{ fontSize: 11.5, color: C.muted }}>El QR se paga con una cuenta de persona natural en Bancolombia o Nequi.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={boton(false)} onClick={() => setPaso('elegir')}>Volver</button>
              <button type="button" style={{ ...boton(true), opacity: terminos && acepto ? 1 : 0.5 }} disabled={!terminos || !acepto} onClick={generar}>Generar QR</button>
            </div>
          </div>
        )}

        {paso === 'generando' && <div style={{ padding: '20px 0', fontSize: 13, color: C.muted }}>Generando el QR con Wompi…</div>}

        {paso === 'qr' && img && (
          <div style={{ fontSize: 13, display: 'grid', gap: 10 }}>
            <img src={img} alt="Código QR de pago" style={{ width: 240, maxWidth: '100%', margin: '0 auto', background: '#fff', padding: 8, borderRadius: 10 }} />
            <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
              <li>Abre la app de <b>Bancolombia</b> o <b>Nequi</b> y entra a <b>Pagar con QR</b>.</li>
              <li>Escanea este código y confirma el pago.</li>
            </ol>
            <div style={{ fontSize: 12, background: `${C.teal}10`, border: `1px solid ${C.teal}33`, borderRadius: 8, padding: '8px 10px' }}>
              <b>¿Estás en este mismo celular?</b> Toca «Guardar QR», abre la app de tu banco y en «Pagar con QR» carga la imagen desde la galería.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" style={boton(false)} onClick={guardar}>Guardar QR</button>
              <span style={{ fontSize: 11.5, color: C.muted }}>Esperando el pago…</span>
            </div>
            <div style={{ fontSize: 12, background: `${C.amber}18`, border: `1px solid ${C.amber}55`, borderRadius: 8, padding: '8px 10px' }}>
              Este QR vence a las <b>{horaVence}</b> (quedan <b>{reloj}</b>). Mientras siga vigente no pagues por otro medio: podrías pagar dos veces.
            </div>
          </div>
        )}

        {paso === 'pagado' && (
          <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
            <div style={{ fontWeight: 700, color: C.teal }}>Wompi confirmó tu pago.</div>
            <button type="button" style={boton(true)} onClick={cerrar}>Listo</button>
          </div>
        )}

        {error && <div style={{ marginTop: 10, fontSize: 12.5, color: '#c0392b' }}>{error}</div>}
        {paso === 'fallo' && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" style={boton(false)} onClick={() => { setError(''); setPaso('elegir'); }}>Volver</button>
          </div>
        )}
      </div>
    </div>
  );
}
